<template>
    <div>
        <div class="rounded-t outside-box-border-bottom" :class="[variantClasses, flashClass]">
            <div class="px-1.5 py-1">
                <slot />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';

type ReviewSectionProps = {
    variant: 'Error' | 'Neutral';
    // Every increment replays the flash, including while one is still running
    flashCount?: number;
};

const props = defineProps<ReviewSectionProps>();

// Keep in sync with the flash-unsaved-* animations below
const flashDurationInMs = 400;

/**
 * Consecutive flashes alternate between two identically animated classes. Re-applying the same one would
 * leave `animation-name` unchanged, which the browser reads as "carry on" — the running animation would play
 * out instead of starting over. Alternating the name is what makes a refusal during a flash flash again.
 */
const flashClass = ref<'flash-unsaved-a' | 'flash-unsaved-b' | null>(null);
let flashTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
    () => props.flashCount,
    () => {
        if (flashTimeout) {
            clearTimeout(flashTimeout);
        }

        flashClass.value = flashClass.value === 'flash-unsaved-a' ? 'flash-unsaved-b' : 'flash-unsaved-a';

        flashTimeout = setTimeout(() => {
            flashClass.value = null;
            flashTimeout = null;
        }, flashDurationInMs);
    },
);

onUnmounted(() => {
    if (flashTimeout) {
        clearTimeout(flashTimeout);
    }
});

const variantClasses = computed(() => {
    switch (props.variant) {
        case 'Error':
            return 'bg-red-700/70 outside-box-border-bottom-red-700-70';

        case 'Neutral':
        default:
            return 'bg-gray-400/70 outside-box-border-bottom-gray-400-70';
    }
});
</script>

<style scoped>
.outside-box-border-bottom {
    position: relative;
}

.outside-box-border-bottom::after {
    position: absolute;
    bottom: -0.5rem;
    height: 0.5rem;
    content: '';
    width: 100%;
    z-index: -10;
}

.outside-box-border-bottom-red-700-70::after {
    background-color: color-mix(in oklab, var(--color-red-700) 70%, transparent); /* = theme('colors.red.700 / 70%'); */
}

.outside-box-border-bottom-gray-400-70::after {
    background-color: color-mix(
        in oklab,
        var(--color-gray-400) 70%,
        transparent
    ); /* = theme('colors.gray.400 / 70%'); */
}

/* The two animations are deliberately identical: only their names differ, so that alternating between them
   restarts the flash. Only the midpoint is declared, which lets it interpolate out of whichever variant
   colour is in play and back again. */
@keyframes flash-unsaved-a {
    50% {
        background-color: var(--color-flash);
    }
}

@keyframes flash-unsaved-b {
    50% {
        background-color: var(--color-flash);
    }
}

/* The ::after paints the strip below the bar, so it has to flash alongside it. It inherits --color-flash. */
.flash-unsaved-a,
.flash-unsaved-b {
    --color-flash: color-mix(in oklab, var(--color-red-700) 70%, transparent);
}

.flash-unsaved-a,
.flash-unsaved-a::after {
    animation: flash-unsaved-a 0.4s ease-in-out;
}

.flash-unsaved-b,
.flash-unsaved-b::after {
    animation: flash-unsaved-b 0.4s ease-in-out;
}

@media (prefers-reduced-motion: reduce) {
    /* Hold the colour instead of dropping the animation entirely: the flash is the only feedback that a
       dismiss was refused, so removing it would leave nothing at all */
    .flash-unsaved-a,
    .flash-unsaved-a::after,
    .flash-unsaved-b,
    .flash-unsaved-b::after {
        animation: none;
        background-color: var(--color-flash);
    }
}
</style>
